import { daysBetween } from '../format/dates'
import type { SkinType } from '../types/catalog'
import type { SegmentRule } from '../types/mail'

/**
 * Doelgroepen: welke abonnee valt erin? Alle regels moeten kloppen. Puur, zodat de demo en de
 * server precies hetzelfde tellen (de server telt; het beheer toont alleen het aantal).
 */

/** Wat de server per abonnee weet. Een abonnee zonder account is "geen klant". */
export type SegmentProfile = {
  isCustomer: boolean
  /** Ooit gekochte producten (betaalde bestellingen). */
  productIds: string[]
  /** Een lopend (actief of gepauzeerd) abonnement. */
  hasSubscription: boolean
  /** Uit het huidadvies of het huidplan; `null` = onbekend. */
  skinType: SkinType | null
  /** Laatste salonbezoek, YYYY-MM-DD; `null` = nooit. */
  lastVisit: string | null
}

/** Maximaal aantal regels per doelgroep: meer is zelden nog een groep. */
export const SEGMENT_RULES_MAX = 5

export function matchesRule(p: SegmentProfile, rule: SegmentRule, today: string): boolean {
  switch (rule.kind) {
    case 'customer':
      return p.isCustomer === rule.value
    case 'boughtProduct':
      return p.productIds.includes(rule.productId)
    case 'subscription':
      return p.hasSubscription === rule.value
    case 'skinType':
      return p.skinType === rule.skinType
    case 'lastVisitWeeks':
      // "Lang niet gezien": ze kwam wel, maar langer dan N weken geleden. Nooit geweest telt niet.
      return p.lastVisit !== null && daysBetween(p.lastVisit, today) > rule.weeks * 7
  }
}

export const matchesSegment = (p: SegmentProfile, rules: SegmentRule[], today: string) => rules.every((r) => matchesRule(p, r, today))

/** Wat er mis is met een regel, of `null`. */
export function ruleProblem(rule: SegmentRule, productExists: (id: string) => boolean): string | null {
  if (rule.kind === 'boughtProduct' && !productExists(rule.productId)) return 'Kies een product dat in de webshop staat'
  if (rule.kind === 'lastVisitWeeks' && (!Number.isInteger(rule.weeks) || rule.weeks < 1 || rule.weeks > 104)) return 'Kies tussen 1 en 104 weken'
  return null
}

/** Twee regels die elkaar uitsluiten maken de groep altijd leeg. */
export function contradiction(rules: SegmentRule[]): string | null {
  const values = (kind: 'customer' | 'subscription') =>
    new Set(rules.flatMap((r) => (r.kind === kind ? [r.value] : [])))
  if (values('customer').size > 1) return 'Klant én geen klant tegelijk: deze groep is altijd leeg'
  if (values('subscription').size > 1) return 'Met én zonder abonnement tegelijk: deze groep is altijd leeg'
  const skins = new Set(rules.flatMap((r) => (r.kind === 'skinType' ? [r.skinType] : [])))
  if (skins.size > 1) return 'Twee huidtypes tegelijk: kies er één per groep'
  if (rules.some((r) => r.kind === 'subscription' && r.value) && rules.some((r) => r.kind === 'customer' && !r.value))
    return 'Een abonnement heeft alleen een klant: deze groep is altijd leeg'
  return null
}
